import React, { useState, useEffect } from 'react';
import SavingsSimulator from './SavingsSimulator';
import TaxGuide from './TaxGuide';
import EducationTimeline from './EducationTimeline';
import { 
  EDUCATION_COSTS, 
  calculateRequiredSavings, 
  createProgressTracker 
} from '../../utils/educationCalculations';
import './EducationSavings.css';

const EducationSavings = ({ 
  monthlyIncome, 
  monthlyExpense, 
  currentSavings = 707667,
  onDataChange 
}) => {
  // 基本設定
  const [childAge, setChildAge] = useState(5); // 年少クラス想定
  const [schoolType, setSchoolType] = useState('public');
  const [currentEducationSavings, setCurrentEducationSavings] = useState(20000); // 月2万円
  const [hasGrandparents, setHasGrandparents] = useState(false);
  
  // 計算結果
  const [requiredSavings, setRequiredSavings] = useState([]);
  const [progressTracker, setProgressTracker] = useState(null);
  const [availableBudget, setAvailableBudget] = useState(0);
  
  // 月間余裕資金の計算
  useEffect(() => {
    const surplus = monthlyIncome - monthlyExpense;
    setAvailableBudget(Math.max(0, surplus));
  }, [monthlyIncome, monthlyExpense]);
  
  // 必要貯蓄額の計算
  useEffect(() => {
    const savings = calculateRequiredSavings(childAge, schoolType);
    setRequiredSavings(savings);
    
    const totalCost = Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0);
    const tracker = createProgressTracker(totalCost, childAge);
    tracker.currentAmount = currentSavings;
    setProgressTracker(tracker);
  }, [childAge, schoolType, currentSavings]);
  
  // データ変更時の親コンポーネントへの通知
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        childAge,
        schoolType,
        currentEducationSavings,
        hasGrandparents,
        totalEducationCost: Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0)
      });
    }
  }, [childAge, schoolType, currentEducationSavings, hasGrandparents, onDataChange]);

  return (
    <div className="education-savings">
      <div className="education-header">
        <h2>教育費貯蓄計画</h2>
        <p className="education-subtitle">
          お子様の将来の教育費を計画的に準備しましょう<br/>
          <small style={{ color: '#e67e22', display: 'block', marginTop: '4px' }}>※現在テスト公開中のベータ版です。計算結果はあくまで目安としてご利用ください。</small>
        </p>
      </div>
      
      {/* 基本設定 */}
      <div className="basic-settings">
        <h3>基本設定</h3>
        <div className="settings-grid">
          <div className="setting-item">
            <label htmlFor="child-age">お子様の現在年齢</label>
            <select 
              id="child-age"
              value={childAge} 
              onChange={(e) => setChildAge(parseInt(e.target.value))}
            >
              {Array.from({length: 18}, (_, i) => (
                <option key={i} value={i}>{i}歳</option>
              ))}
            </select>
          </div>
          
          <div className="setting-item">
            <label htmlFor="school-type">進学予定</label>
            <select 
              id="school-type"
              value={schoolType} 
              onChange={(e) => setSchoolType(e.target.value)}
            >
              <option value="public">公立中心</option>
              <option value="private">私立中心</option>
            </select>
          </div>
          
          <div className="setting-item">
            <label htmlFor="current-education-savings">現在の教育費貯金（月額）</label>
            <input 
              type="number" 
              id="current-education-savings"
              value={currentEducationSavings}
              onChange={(e) => setCurrentEducationSavings(parseInt(e.target.value) || 0)}
              min="0"
              step="1000"
            />
            <span className="unit">円</span>
          </div>
          
          <div className="setting-item">
            <label>
              <input 
                type="checkbox"
                checked={hasGrandparents}
                onChange={(e) => setHasGrandparents(e.target.checked)}
              />
              祖父母からの支援が期待できる
            </label>
          </div>
        </div>
      </div>
      
      {/* 収支サマリー */}
      <div className="budget-summary">
        <h3>収支サマリー</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">月間収入</span>
            <span className="value income">{monthlyIncome?.toLocaleString() || 0}円</span>
          </div>
          <div className="summary-item">
            <span className="label">月間支出</span>
            <span className="value expense">{monthlyExpense?.toLocaleString() || 0}円</span>
          </div>
          <div className="summary-item">
            <span className="label">月間余裕資金</span>
            <span className={`value ${availableBudget > 0 ? 'surplus' : 'deficit'}`}>
              {availableBudget.toLocaleString()}円
            </span>
          </div>
          <div className="summary-item">
            <span className="label">現在の教育費貯金</span>
            <span className="value">{currentEducationSavings.toLocaleString()}円/月</span>
          </div>
        </div>
      </div>
      
      {/* 教育費概要 */}
      <div className="education-overview">
        <h3>教育費概要（{schoolType === 'public' ? '公立中心' : '私立中心'}）</h3>
        <div className="cost-breakdown">
          {Object.entries(EDUCATION_COSTS[schoolType]).map(([stage, cost]) => {
            const stageNames = {
              elementary: '小学校',
              junior: '中学校', 
              senior: '高校',
              university: '大学'
            };
            
            return (
              <div key={stage} className="cost-item">
                <span className="stage-name">{stageNames[stage]}</span>
                <span className="stage-cost">{(cost / 10000).toFixed(1)}万円</span>
              </div>
            );
          })}
        </div>
        <div className="total-cost">
          <strong>
            合計: {(Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0) / 10000).toFixed(1)}万円
          </strong>
        </div>
      </div>
      
      {/* タイムライン */}
      <EducationTimeline 
        requiredSavings={requiredSavings}
        currentAge={childAge}
        progressTracker={progressTracker}
      />
      
      {/* 貯蓄シミュレーター */}
      <SavingsSimulator 
        currentSavings={currentSavings}
        monthlyBudget={availableBudget}
        currentEducationSavings={currentEducationSavings}
        childAge={childAge}
        schoolType={schoolType}
        onSavingsChange={setCurrentEducationSavings}
      />
      
      {/* 税制ガイド */}
      <TaxGuide 
        totalEducationCosts={Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0)}
        hasGrandparents={hasGrandparents}
      />
      
      {/* 進捗アラート */}
      {progressTracker && (
        <div className="progress-alerts">
          <h3>進捗アラート</h3>
          <div className="alerts-container">
            {/* 目標達成率チェック */}
            {(() => {
              const totalCost = Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0);
              const achievementRate = (progressTracker.currentAmount / totalCost) * 100;
              
              if (achievementRate < 30) {
                return (
                  <div className="alert alert-warning">
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-content">
                      <h4>貯蓄ペースの見直しが必要です</h4>
                      <p>現在の進捗率は{achievementRate.toFixed(1)}%です。目標達成のため、月間貯蓄額の増額を検討してください。</p>
                    </div>
                  </div>
                );
              } else if (achievementRate >= 80) {
                return (
                  <div className="alert alert-success">
                    <span className="alert-icon">🎉</span>
                    <div className="alert-content">
                      <h4>順調に進捗しています！</h4>
                      <p>現在の進捗率は{achievementRate.toFixed(1)}%です。この調子で継続してください。</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* 年齢制限アラート */}
            {childAge >= 15 && (
              <div className="alert alert-info">
                <span className="alert-icon">⏰</span>
                <div className="alert-content">
                  <h4>ジュニアNISAの期限が近づいています</h4>
                  <p>18歳まで引き出し制限があるため、計画的な活用を検討してください。</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* アクションプラン */}
      <div className="action-plan">
        <h3>推奨アクションプラン</h3>
        <div className="recommendations">
          {availableBudget > currentEducationSavings && (
            <div className="recommendation">
              <h4>💡 貯蓄額増額の検討</h4>
              <p>
                現在の余裕資金（{availableBudget.toLocaleString()}円）から、
                教育費貯金を月{Math.min(50000, availableBudget - currentEducationSavings).toLocaleString()}円
                まで増額することが可能です。
              </p>
            </div>
          )}
          
          {hasGrandparents && (
            <div className="recommendation">
              <h4>🎁 祖父母支援の活用</h4>
              <p>
                教育費の直接支払いを祖父母にお願いすることで、
                贈与税を気にせず支援を受けることができます。
              </p>
            </div>
          )}
          
          <div className="recommendation">
            <h4>📈 投資の検討</h4>
            <p>
              つみたてNISAやジュニアNISAを活用することで、
              長期的により効率的に教育資金を準備できる可能性があります。
            </p>
          </div>
          
          {/* 定期見直しの推奨 */}
          <div className="recommendation">
            <h4>📅 定期見直しの実施</h4>
            <p>
              年1回は教育費計画を見直し、収入変化や教育方針の変更に合わせて
              貯蓄戦略を調整することをお勧めします。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationSavings;
